import { useEffect, useRef, useState } from 'react';
import { BookOpen, Check, ChevronDown, ClipboardCheck } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

type AppKey = 'courses' | 'tests';

const apps: Array<{ key: AppKey; label: string; description: string; path: string; icon: typeof BookOpen }> = [
  { key: 'courses', label: 'Courses', description: 'Catalog and learner content', path: '/admin', icon: BookOpen },
  { key: 'tests', label: 'Tests', description: 'Question bank and assessments', path: '/test-admin', icon: ClipboardCheck },
];

export function AppSwitcher({ current }: { current: AppKey }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const close = (event: MouseEvent) => { if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false); };
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', escape); };
  }, []);
  const activeApp = apps.find((app) => app.key === current) || apps[0];
  const ActiveIcon = activeApp.icon;
  return <div className="app-switcher" ref={rootRef}>
    <button className="app-switcher-trigger" type="button" aria-haspopup="menu" aria-expanded={open} aria-label={`Current app: ${activeApp.label}. Switch app`} onClick={() => setOpen((value) => !value)}>
      <ActiveIcon size={16} />
      <span>{activeApp.label}</span>
      <ChevronDown size={14} className={open ? 'is-open' : ''} />
    </button>
    {open && <div className="app-switcher-menu" role="menu" aria-label="Switch app">
      <span className="app-switcher-heading">Switch app</span>
      {apps.map(({ key, label, description, path, icon: Icon }) => <button className={`app-switcher-option ${key === current ? 'active' : ''}`} key={key} type="button" role="menuitem" onClick={() => { setOpen(false); if (location.pathname !== path) navigate(path); }}>
        <span className={`app-switcher-icon ${key}`}><Icon size={16} /></span>
        <span className="app-switcher-copy"><strong>{label}</strong><small>{description}</small></span>
        {key === current && <Check size={15} className="app-switcher-check" />}
      </button>)}
    </div>}
  </div>;
}
